import type {
  Bookmark,
  BookmarkType,
  Folder,
  UserBookmarks,
  WorkEngagementBookmarks,
} from '../types';

export interface FolderPath {
  readonly folder: Folder;
  readonly index: number;
}

export type FolderIndexPath = readonly number[];

export function getBookmarkPath(bookmark: Bookmark): string {
  if (
    !bookmark.query_params ||
    Object.keys(bookmark.query_params).length === 0
  ) {
    return bookmark.path;
  }

  const search = new URLSearchParams(bookmark.query_params).toString();
  return `${bookmark.path}?${search}`;
}

/**
 * Extracts the index array from a FolderPath array.
 * Useful when you need to convert between the two path representations.
 * @deprecated Use FolderIndexPath (number[]) directly instead.
 */
export function toIndexPath(path: readonly FolderPath[]): FolderIndexPath {
  return path.map((p) => p.index);
}

/**
 * Gets all folders along a path, useful for breadcrumb display.
 *
 * @param folders - The root folders array
 * @param path - Array of indices representing the navigation path
 * @returns Array of folders at each level of the path
 *
 * @example
 * ```ts
 * const pathFolders = getFoldersAlongPath(folders, [0, 2, 1]);
 * // Returns [folder at 0, folder at 0->2, folder at 0->2->1]
 * ```
 */
export function getFoldersAlongPath(
  folders: Folder[],
  path: FolderIndexPath
): Folder[] {
  const result: Folder[] = [];
  let currentFolders = folders;

  for (const index of path) {
    const folder = currentFolders[index];
    if (!folder) break;
    result.push(folder);
    currentFolders = folder.child_folders ?? [];
  }

  return result;
}

/**
 * Retrieves a folder at a specific path.
 *
 * @param folders - The root folders array to search within
 * @param path - Array of indices leading to the target folder
 * @returns The folder at the path, or undefined if not found
 *
 * @example
 * ```ts
 * const folder = getFolderAtPath(userBookmarks.folders, [0, 2]);
 * if (folder) {
 *   console.log(folder.name);
 * }
 * ```
 */
export function getFolderAtPath(
  folders: Folder[],
  path: FolderIndexPath
): Folder | undefined {
  if (path.length === 0) return undefined;

  const [currentIndex, ...remainingPath] = path;
  const currentFolder = folders[currentIndex];

  if (!currentFolder) return undefined;
  if (remainingPath.length === 0) return currentFolder;

  return getFolderAtPath(currentFolder.child_folders ?? [], remainingPath);
}

/**
 * Validates whether a folder path exists in the folder tree.
 *
 * @param folders - The root folders array to search within
 * @param path - Array of indices to validate
 * @returns True if the path leads to a valid folder
 */
export function isValidFolderPath(
  folders: Folder[],
  path: FolderIndexPath
): boolean {
  if (path.length === 0) return true;

  const [currentIndex, ...remainingPath] = path;
  const currentFolder = folders[currentIndex];

  if (!currentFolder) return false;
  if (remainingPath.length === 0) return true;

  return isValidFolderPath(currentFolder.child_folders ?? [], remainingPath);
}

// ============================================================================
// Tree Modification Utilities
// ============================================================================

/**
 * Recursively updates a folder at a specific path in the folder tree.
 *
 * @param folders - The root folders array to search within
 * @param path - Array of indices leading to the target folder
 * @param updater - Function that receives the target folder and returns the updated folder
 * @returns A new folders array with the update applied, or the original if path is empty
 *
 * @remarks
 * When path is empty, the original folders array is returned unchanged.
 * This is intentional as there's no target folder to update.
 *
 * @example
 * ```ts
 * const updated = updateFolderInTree(folders, [0, 2], (f) => ({
 *   ...f,
 *   name: 'New Name'
 * }));
 * ```
 */
export function updateFolderInTree(
  folders: Folder[],
  path: FolderIndexPath,
  updater: (folder: Folder) => Folder
): Folder[] {
  if (path.length === 0) {
    return folders;
  }

  const [currentIndex, ...remainingPath] = path;

  return folders.map((folder, index) => {
    if (index !== currentIndex) {
      return folder;
    }

    if (remainingPath.length === 0) {
      return updater(folder);
    }

    return {
      ...folder,
      child_folders: updateFolderInTree(
        folder.child_folders ?? [],
        remainingPath,
        updater
      ),
    };
  });
}

/**
 * Recursively removes a folder from the folder tree at a specific path.
 *
 * @param folders - The folders array to search in
 * @param path - Array of indices to the parent folder (empty array for root)
 * @param folderIndex - Index of the folder to delete within the parent
 * @returns A new folders array with the folder removed
 */
export function deleteFolderFromTree(
  folders: Folder[],
  path: FolderIndexPath,
  folderIndex: number
): Folder[] {
  if (path.length === 0) {
    return folders.filter((_, index) => index !== folderIndex);
  }

  const [currentIndex, ...remainingPath] = path;

  return folders.map((folder, index) => {
    if (index !== currentIndex) {
      return folder;
    }

    if (remainingPath.length === 0) {
      return {
        ...folder,
        child_folders: (folder.child_folders ?? []).filter(
          (_, idx) => idx !== folderIndex
        ),
      };
    }

    return {
      ...folder,
      child_folders: deleteFolderFromTree(
        folder.child_folders ?? [],
        remainingPath,
        folderIndex
      ),
    };
  });
}

/**
 * Recursively removes a bookmark from a folder at a specific path.
 *
 * @remarks
 * This function only operates on bookmarks within folders, not root-level bookmarks.
 * When path is empty, the original folders array is returned unchanged.
 * Use {@link removeBookmarkFromLocation} to handle both root and nested bookmarks.
 */
export function deleteBookmarkFromTree(
  folders: Folder[],
  path: FolderIndexPath,
  bookmarkIndex: number
): Folder[] {
  if (path.length === 0) {
    // This function only handles folder-nested bookmarks.
    // Root bookmarks should be handled by removeBookmarkFromLocation.
    return folders;
  }

  const [currentIndex, ...remainingPath] = path;

  return folders.map((folder, index) => {
    if (index !== currentIndex) {
      return folder;
    }

    if (remainingPath.length === 0) {
      return {
        ...folder,
        child_bookmarks: (folder.child_bookmarks ?? []).filter(
          (_, idx) => idx !== bookmarkIndex
        ),
      };
    }

    return {
      ...folder,
      child_folders: deleteBookmarkFromTree(
        folder.child_folders ?? [],
        remainingPath,
        bookmarkIndex
      ),
    };
  });
}

// ============================================================================
// Bookmark Retrieval Utilities
// ============================================================================

/**
 * Gets all bookmarks from nested folders recursively.
 *
 * @remarks
 * This does NOT include root-level bookmarks from UserBookmarks.bookmarks.
 * Use {@link getAllBookmarks} to get all bookmarks including root level.
 */
export function getAllNestedBookmarks(folders: Folder[]): Bookmark[] {
  return folders.flatMap((folder) => [
    ...(folder.child_bookmarks ?? []),
    ...getAllNestedBookmarks(folder.child_folders ?? []),
  ]);
}

/**
 * Gets all bookmarks including root-level and nested bookmarks.
 */
export function getAllBookmarks(
  userBookmarks: Omit<UserBookmarks, 'customer_id' | 'user_id'> | undefined
): Bookmark[] {
  if (!userBookmarks) return [];

  return [
    ...userBookmarks.bookmarks,
    ...getAllNestedBookmarks(userBookmarks.folders),
  ];
}

/**
 * Computes the maximum order value across all bookmarks (root and nested).
 * Returns 0 if there are no bookmarks.
 */
export function getMaxBookmarkOrder<
  T extends { bookmarks?: Bookmark[]; folders?: Folder[] },
>(bookmarksData: T | undefined): number {
  if (!bookmarksData) return 0;

  let max = Number.NEGATIVE_INFINITY;

  function scanFolders(folders: Folder[] | undefined) {
    for (const folder of folders ?? []) {
      for (const b of folder.child_bookmarks ?? []) {
        if (b.order > max) {
          max = b.order;
        }
      }
      scanFolders(folder.child_folders);
    }
  }

  for (const b of bookmarksData.bookmarks ?? []) {
    if (b.order > max) {
      max = b.order;
    }
  }

  scanFolders(bookmarksData.folders);

  return max === Number.NEGATIVE_INFINITY ? 0 : max;
}

export function getMaxFolderOrder(
  userBookmarks: Omit<UserBookmarks, 'customer_id' | 'user_id'> | undefined
): number {
  if (!userBookmarks) {
    return 0;
  }

  let max = Number.NEGATIVE_INFINITY;

  function scanFolders(folders: Folder[] | undefined) {
    for (const folder of folders ?? []) {
      for (const f of folder.child_folders ?? []) {
        if (f.order > max) {
          max = f.order;
        }
      }
      scanFolders(folder.child_folders);
    }
  }

  for (const b of userBookmarks.folders ?? []) {
    if (b.order > max) {
      max = b.order;
    }
  }

  scanFolders(userBookmarks.folders);

  return max === Number.NEGATIVE_INFINITY ? 0 : max;
}

// ============================================================================
// Location-based Operations
// ============================================================================

/**
 * Creates an empty UserBookmarks object with the provided user identifiers.
 */
export function createEmptyUserBookmarks(): Omit<
  UserBookmarks,
  'customer_id' | 'user_id'
> {
  return {
    bookmarks: [],
    folders: [],
    folders_shared_with_me: [],
    subscribed_work_engagements: [],
  };
}

/**
 * Creates an empty WorkEngagementBookmarks object with the provided work engagement identifiers.
 */
export function createEmptyWorkEngagementBookmarks(): Omit<
  WorkEngagementBookmarks,
  'customer_id' | 'work_engagement_id'
> {
  return {
    bookmarks: [],
    folders: [],
  };
}

/**
 * Adds a bookmark to a specific folder location.
 *
 * @param bookmarks - The current user or work engagement bookmarks, or undefined to create new
 * @param bookmark - The bookmark data (order will be auto-calculated)
 * @param folderPath - Array of indices to the target folder (empty for root level)
 * @returns Updated UserBookmarks or WorkEngagementBookmarks with the new bookmark added
 *
 * @example
 * ```ts
 * // Add to root
 * const updated = addBookmarkToLocation(userBookmarks, bookmark);
 *
 * // Add to nested folder at path [0, 2]
 * const updated = addBookmarkToLocation(userBookmarks, bookmark, [0, 2]);
 * ```
 */
export function addBookmarkToLocation<
  T extends { bookmarks: Bookmark[]; folders: Folder[] },
>(
  bookmarks: T | undefined,
  bookmark: Omit<Bookmark, 'user_id' | 'customer_id' | 'order'>,
  folderPath: FolderIndexPath = [],
  type?: BookmarkType | null
) {
  const maxOrder = getMaxBookmarkOrder(bookmarks);

  const newBookmark: Bookmark = {
    ...bookmark,
    order: maxOrder + 1,
  };

  if (!bookmarks) {
    const base =
      type === 'workengagement'
        ? createEmptyWorkEngagementBookmarks()
        : createEmptyUserBookmarks();

    if (folderPath.length > 0) {
      // Cannot add to a folder path when there are no folders
      console.warn(
        'addBookmarkToLocation: Cannot add bookmark to folder path when creating new UserBookmarks'
      );
    }

    return { ...base, bookmarks: [newBookmark] };
  }

  if (folderPath.length === 0) {
    return {
      ...bookmarks,
      bookmarks: [...bookmarks.bookmarks, newBookmark],
    };
  }

  return {
    ...bookmarks,
    folders: updateFolderInTree(bookmarks.folders, folderPath, (folder) => ({
      ...folder,
      child_bookmarks: [...(folder.child_bookmarks ?? []), newBookmark],
    })),
  };
}

/**
 * Adds a folder to a specific location in the folder hierarchy.
 *
 * @param bookmarks - The current user or work engagement bookmarks, or undefined to create new
 * @param newFolder - The folder to add
 * @param folderPath - Array of indices to the parent folder (empty for root level)
 * @returns Updated UserBookmarks or WorkEngagementBookmarks with the new folder added
 */

export function addFolderToLocation<T extends { folders: Folder[] }>(
  bookmarks: T | undefined,
  newFolder: Folder,
  folderPath: FolderIndexPath = [],
  type?: BookmarkType | null
) {
  if (!bookmarks) {
    const base =
      type === 'workengagement'
        ? createEmptyWorkEngagementBookmarks()
        : createEmptyUserBookmarks();

    if (folderPath.length > 0) {
      // Cannot add to a nested path when there are no folders
      console.warn(
        'addFolderToLocation: Cannot add folder to nested path when creating new UserBookmarks'
      );
    }

    return { ...base, folders: [newFolder] };
  }

  if (folderPath.length === 0) {
    return {
      ...bookmarks,
      folders: [...bookmarks.folders, newFolder],
    };
  }

  const updatedFolders = updateFolderInTree(
    bookmarks.folders,
    folderPath,
    (folder) => ({
      ...folder,
      child_folders: [...(folder.child_folders ?? []), newFolder],
    })
  );

  return {
    ...bookmarks,
    folders: updatedFolders,
  };
}

/**
 * Removes a bookmark from a specific location.
 *
 * @param userBookmarks - The current user bookmarks
 * @param folderPath - Array of indices to the containing folder (empty for root level)
 * @param bookmarkIndex - Index of the bookmark to remove
 * @returns Updated UserBookmarks with the bookmark removed
 */
export function removeBookmarkFromLocation(
  userBookmarks: Omit<UserBookmarks, 'customer_id' | 'user_id'>,
  folderPath: FolderIndexPath,
  bookmarkIndex: number
): Omit<UserBookmarks, 'customer_id' | 'user_id'> {
  if (folderPath.length === 0) {
    return {
      ...userBookmarks,
      bookmarks: userBookmarks.bookmarks.filter(
        (_, idx) => idx !== bookmarkIndex
      ),
    };
  }

  const updatedFolders = deleteBookmarkFromTree(
    userBookmarks.folders,
    folderPath,
    bookmarkIndex
  );

  return {
    ...userBookmarks,
    folders: updatedFolders,
  };
}

/**
 * Removes a folder from a specific location.
 *
 * @param userBookmarks - The user bookmarks object
 * @param folderPath - Array of indices to the parent folder (empty array for root)
 * @param folderIndex - Index of the folder to delete within the parent
 * @returns Updated UserBookmarks with the folder removed
 */
export function removeFolderFromLocation(
  userBookmarks: Omit<UserBookmarks, 'customer_id' | 'user_id'>,
  folderPath: FolderIndexPath,
  folderIndex: number
): Omit<UserBookmarks, 'customer_id' | 'user_id'> {
  const updatedFolders = deleteFolderFromTree(
    userBookmarks.folders,
    folderPath,
    folderIndex
  );

  return {
    ...userBookmarks,
    folders: updatedFolders,
  };
}

export function deleteById(folders: Folder[], idToDelete: string): Folder[] {
  return folders
    .filter((folder) => folder.client_id !== idToDelete)
    .map((folder) => ({
      ...folder,
      child_bookmarks: (folder.child_bookmarks || []).filter(
        (bm) => !(bm && bm.client_id === idToDelete)
      ),
      child_folders: folder.child_folders
        ? deleteById(folder.child_folders, idToDelete)
        : folder.child_folders,
    }));
}

export function editById(
  folders: Folder[],
  idToEdit: string,
  editedItem: Partial<Folder> | Partial<Bookmark>
): Folder[] {
  return folders.map((folder) => {
    if (folder.client_id === idToEdit) {
      return {
        ...folder,
        ...editedItem,
      } as Folder;
    }

    const updatedBookmarks = (folder.child_bookmarks || []).map((bm) => {
      if (bm.client_id === idToEdit) {
        return {
          ...bm,
          ...editedItem,
        } as Bookmark;
      }
      return bm;
    });

    const updatedFolders = folder.child_folders
      ? editById(folder.child_folders, idToEdit, editedItem)
      : folder.child_folders;

    return {
      ...folder,
      child_bookmarks: updatedBookmarks,
      child_folders: updatedFolders,
    };
  });
}

export function removeSharedFolderById(
  foldersSharedWithMe: Folder[],
  idToRemove: string
): Folder[] {
  return foldersSharedWithMe.filter(
    (folder) => folder.client_id !== idToRemove
  );
}

export function findSharedFolderById(
  foldersSharedWithMe: Folder[],
  id: string
): Folder | undefined {
  return foldersSharedWithMe.find((folder) => folder.client_id === id);
}

// ============================================================================
// Move Operations
// ============================================================================

/**
 * Moves a bookmark from one location to another.
 *
 * @param userBookmarks - The current user bookmarks
 * @param fromPath - Array of indices to the source folder (empty for root)
 * @param fromIndex - Index of the bookmark in the source location
 * @param toPath - Array of indices to the destination folder (empty for root)
 * @returns Updated UserBookmarks with the bookmark moved, or original if bookmark not found
 *
 * @example
 * ```ts
 * // Move from root to a folder at index [0, 2]
 * const updated = moveBookmark(userBookmarks, [], 0, [0, 2]);
 *
 * // Move from folder to root
 * const updated = moveBookmark(userBookmarks, [1, 3], 2, []);
 * ```
 */
export function moveBookmark(
  userBookmarks: Omit<UserBookmarks, 'customer_id' | 'user_id'>,
  fromPath: FolderIndexPath,
  fromIndex: number,
  toPath: FolderIndexPath
): Omit<UserBookmarks, 'customer_id' | 'user_id'> {
  // Get the bookmark from source location
  let bookmark: Bookmark | undefined;

  if (fromPath.length === 0) {
    bookmark = userBookmarks.bookmarks[fromIndex];
  } else {
    const folder = getFolderAtPath(userBookmarks.folders, fromPath);
    bookmark = folder?.child_bookmarks?.[fromIndex];
  }

  if (!bookmark) {
    console.warn('moveBookmark: Source bookmark not found');
    return userBookmarks;
  }

  // Remove from old location
  const afterRemoval = removeBookmarkFromLocation(
    userBookmarks,
    fromPath,
    fromIndex
  );

  // Add to new location (preserving the bookmark's properties except order)
  const { order: _order, ...bookmarkWithoutOrder } = bookmark;

  return addBookmarkToLocation(
    afterRemoval,
    bookmarkWithoutOrder,
    toPath
  ) as Omit<UserBookmarks, 'customer_id' | 'user_id'>;
}

/**
 * Moves a folder from one location to another.
 *
 * @param userBookmarks - The current user bookmarks
 * @param fromPath - Array of indices to the parent of the source folder (empty for root)
 * @param fromIndex - Index of the folder in the source location
 * @param toPath - Array of indices to the destination parent folder (empty for root)
 * @returns Updated UserBookmarks with the folder moved, or original if folder not found
 */
export function moveFolder(
  userBookmarks: Omit<UserBookmarks, 'customer_id' | 'user_id'>,
  fromPath: FolderIndexPath,
  fromIndex: number,
  toPath: FolderIndexPath
): Omit<UserBookmarks, 'customer_id' | 'user_id'> {
  // Get the folder from source location
  let folder: Folder | undefined;

  if (fromPath.length === 0) {
    folder = userBookmarks.folders[fromIndex];
  } else {
    const parentFolder = getFolderAtPath(userBookmarks.folders, fromPath);
    folder = parentFolder?.child_folders?.[fromIndex];
  }

  if (!folder) {
    console.warn('moveFolder: Source folder not found');
    return userBookmarks;
  }

  // Remove from old location
  const afterRemoval = removeFolderFromLocation(
    userBookmarks,
    fromPath,
    fromIndex
  );

  // Add to new location
  return addFolderToLocation(afterRemoval, folder, toPath) as Omit<
    UserBookmarks,
    'customer_id' | 'user_id'
  >;
}
